import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserDelete } from '@/utils/permissions';
import { 
  Package, Search, Plus, AlertTriangle, AlertCircle, CheckCircle, 
  RefreshCw, Filter, ChevronDown, X, Minus, FileText,
  Truck, User, Building2, Loader2, Trash2, Upload, Download, ShieldAlert, Scale
} from 'lucide-react';

function ConsumableStock({ poType = 'material' }) {
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const isService = poType === 'service';
  const pageLabel = isService ? 'Service Consumption' : 'Consumable Stock';
  const baseRoute = isService ? '/stores/service-consumption' : '/stores/consumable-stock';
  const [stockData, setStockData] = useState([]);
  const [issueHistory, setIssueHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState(null);
  
  // Filters
  const [itemFilter, setItemFilter] = useState('all');
  const [partyFilter, setPartyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [consumptionSearch, setConsumptionSearch] = useState('');
  // Tools dropdown (Monthly Report / Compare Months / Export CSV / Bulk Upload)
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  
  // Material Issue Modal
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [issueForm, setIssueForm] = useState({
    quantity: '',
    issuedTo: '',
    department: '',
    purpose: '',
    remarks: ''
  });
  const [issuing, setIssuing] = useState(false);
  
  // Unique values for filters
  const [uniqueItems, setUniqueItems] = useState([]);
  const [uniqueParties, setUniqueParties] = useState([]);
  
  // Permissions
  const canDelete = canUserDelete(user, '/stores/consumable-stock');
  
  // Bulk Upload Modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadMode, setUploadMode] = useState('csv'); // 'csv' or 'stock-report'
  const fileInputRef = useRef(null);
  const stockReportInputRef = useRef(null);
  
  // Monthly Report
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [activeTab, setActiveTab] = useState('stock');
  const [consumptionData, setConsumptionData] = useState([]);
  const [consumptionPeriod, setConsumptionPeriod] = useState('month');
  const [loadingConsumption, setLoadingConsumption] = useState(false);
  // Month/Year selector for the consumption report
  const _now = new Date();
  const [reportMonth, setReportMonth] = useState(_now.getMonth() + 1);
  const [reportYear, setReportYear] = useState(_now.getFullYear());

  // Compare-Months modal state
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [baseMonth, setBaseMonth] = useState(_now.getMonth() === 0 ? 12 : _now.getMonth());
  const [baseYear, setBaseYear] = useState(_now.getMonth() === 0 ? _now.getFullYear() - 1 : _now.getFullYear());
  const [cmpMonth, setCmpMonth] = useState(_now.getMonth() + 1);
  const [cmpYear, setCmpYear] = useState(_now.getFullYear());

  const handleLoadCompare = async () => {
    setCompareLoading(true);
    try {
      const url = `${API}/stores/consumable-stock/monthly-report/compare?baseMonth=${baseMonth}&baseYear=${baseYear}&compareMonth=${cmpMonth}&compareYear=${cmpYear}`;
      const res = await axios.get(url);
      setCompareData(res.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load comparison' });
    } finally {
      setCompareLoading(false);
    }
  };

  const openCompareModal = () => {
    setShowCompareModal(true);
    if (!compareData) handleLoadCompare();
  };

  // Export the live Stock Inventory rows (incl. the bulk/GRN/issued breakdown)
  // to a CSV file the user can paste into board reports.
  const handleExportCsv = () => {
    const headers = [
      'Item Name', 'HSN', 'Category', 'Unit',
      'Bulk Baseline', 'GRN Inflow', 'Issued', 'Available',
      'Rate (₹)', 'Value (₹)', 'Status', 'Last GRN', 'Vendor', 'Source',
    ];
    const csvEscape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = (filteredData || []).map((it) => {
      const bd = it.breakdown || {};
      return [
        it.itemName, it.hsnCode || '', it.category || '', it.unit || '',
        (bd.bulkBaseline || 0).toFixed(2),
        (bd.lotReceived || 0).toFixed(2),
        (bd.totalConsumed || 0).toFixed(2),
        (it.availableQty || 0).toFixed(2),
        (it.weightedAvgRate || it.rate || 0).toFixed(2),
        (it.totalValue || it.value || 0).toFixed(2),
        it.status || '',
        it.lastGRN || '',
        it.party || '',
        it.source || '',
      ].map(csvEscape).join(',');
    });
    const ts = new Date().toISOString().slice(0, 10);
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consumable_stock_breakdown_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Stock-health passive notification — fires once on mount, returns counts
  // so we can paint a badge on the "Stock Health" button.
  const [healthIssueCount, setHealthIssueCount] = useState(null);

  useEffect(() => {
    fetchStockData();
    fetchIssueHistory();
    // Stock-health probe — quiet, no-throw, doesn't block first paint
    axios.get(`${API}/stores/consumable-stock/health?poType=${poType}`)
      .then((r) => {
        const s = r.data?.summary || {};
        const total = (s.orphanLots || 0) + (s.orphanInventory || 0) + (s.orphanIssues || 0) + (s.stockDrift || 0);
        setHealthIssueCount(total);
      })
      .catch(() => setHealthIssueCount(null));
  }, []);

  const fetchStockData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stores/consumable-stock?poType=${poType}`);
      const data = response.data.data || response.data || [];
      
      setStockData(data);
      setLastSyncTime(response.data.lastSyncTime || null);
      
      // Extract unique values for filters
      const items = [...new Set(data.map(d => d.itemName).filter(Boolean))];
      const parties = [...new Set(data.map(d => d.party).filter(Boolean))];
      
      setUniqueItems(items.sort());
      setUniqueParties(parties.sort());
    } catch (error) {
      console.error('Error fetching consumable stock:', error);
      setMessage({ type: 'error', text: 'Failed to load stock data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchIssueHistory = async () => {
    try {
      const response = await axios.get(`${API}/stores/material-issues`);
      setIssueHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching issue history:', error);
    }
  };

  const fetchConsumptionData = async (period) => {
    setLoadingConsumption(true);
    try {
      const response = await axios.get(
        `${API}/stores/consumable-stock/consumption-analytics?period=${period || consumptionPeriod}&poType=${poType}`
      );
      setConsumptionData(response.data);
    } catch (error) {
      console.error('Error fetching consumption data:', error);
    } finally {
      setLoadingConsumption(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'consumption') {
      fetchConsumptionData(consumptionPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, consumptionPeriod, poType]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Stock': return 'bg-green-100 text-green-700 border-green-200';
      case 'Low Stock': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Out of Stock': return 'bg-red-100 text-red-700 border-red-200';
      case 'Critical': return 'bg-red-100 text-red-700 border-red-200 animate-pulse';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'In Stock': return <CheckCircle size={14} className="text-green-600" />;
      case 'Low Stock': return <AlertTriangle size={14} className="text-amber-600" />;
      case 'Out of Stock': 
      case 'Critical': return <AlertTriangle size={14} className="text-red-600" />;
      default: return null;
    }
  };

  // Apply filters
  const filteredData = stockData.filter(item => {
    const itemName = item.itemName || '';
    const party = item.party || '';
    
    const matchesSearch = 
      itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.itemCode || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      party.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesItem = itemFilter === 'all' || itemName === itemFilter;
    const matchesParty = partyFilter === 'all' || party === partyFilter;
    const matchesStatus = statusFilter === 'all'
      || (statusFilter === 'phantom-only' ? item.isPhantomConsumption === true : item.status === statusFilter);
    
    return matchesSearch && matchesItem && matchesParty && matchesStatus;
  });

  // Filter consumption data by department
  const filteredConsumptionData = useMemo(() => {
    if (!consumptionData) return consumptionData;
    const term = consumptionSearch.trim().toLowerCase();
    const noDept = deptFilter === 'all';
    if (noDept && !term) return consumptionData;

    const filtered = (consumptionData.data || []).map(item => {
      // Department filter on per-issue level (when active)
      let issues = item.issues || [];
      if (!noDept) {
        issues = issues.filter(iss => iss.department === deptFilter);
        if (issues.length === 0) return null;
      }
      // Search filter on commodity name (case-insensitive substring).
      // Backend returns `commodityName` — check it first, with fallbacks
      // for any legacy / shape variations.
      const nameForSearch = (item.commodityName || item.commodity || item.itemName || item.materialName || '').toLowerCase();
      if (term && !nameForSearch.includes(term)) {
        return null;
      }
      const totalQty = issues.reduce((sum, i) => sum + (i.quantity || 0), 0);
      // Use the backend's per-issue `value` (which already runs the smart
      // rate-fallback chain) instead of recomputing `qty * rate`. Issues
      // don't carry a top-level `rate` field — that's why prior reduce
      // gave ₹0 on every filtered row even when the data was fine.
      const totalValue = issues.reduce((sum, i) => sum + (i.value || 0), 0);
      const avgRate = totalQty > 0 ? totalValue / totalQty : 0;
      return {
        ...item,
        issues,
        issueCount: issues.length,
        totalQty,
        totalValue,
        avgRate,
        rateMissing: avgRate === 0,
        rateSource: item.rateSource || null,
        // Keep `departments` as plain strings — React refuses to render
        // objects as children; this caused the page-blank bug 2026-05-27.
        departments: [...new Set(issues.map(i => i.department).filter(Boolean))],
      };
    }).filter(Boolean);
    return {
      ...consumptionData,
      data: filtered,
      totalCommodities: filtered.length,
      totalIssues: filtered.reduce((sum, i) => sum + i.issueCount, 0),
      grandTotalQty: filtered.reduce((sum, i) => sum + i.totalQty, 0),
      grandTotalValue: filtered.reduce((sum, i) => sum + i.totalValue, 0),
    };
  }, [consumptionData, deptFilter, consumptionSearch]);

  // Stats
  const stats = {
    total: stockData.length,
    inStock: stockData.filter(i => i.status === 'In Stock').length,
    lowStock: stockData.filter(i => i.status === 'Low Stock' || i.status === 'Out of Stock').length,
    totalValue: stockData.reduce((sum, i) => sum + (i.totalValue || i.value || 0), 0),
    totalAvailable: stockData.reduce((sum, i) => sum + (i.availableQty || 0), 0),
    phantom: stockData.filter(i => i.isPhantomConsumption).length,
  };

  // Clear all filters
  const clearFilters = () => {
    setItemFilter('all');
    setPartyFilter('all');
    setStatusFilter('all');
    setSearchTerm('');
  };

  const hasActiveFilters = itemFilter !== 'all' || partyFilter !== 'all' || statusFilter !== 'all' || searchTerm;

  // Open Issue Modal
  const openIssueModal = (item) => {
    setSelectedItem(item);
    setIssueForm({
      quantity: '',
      issuedTo: '',
      department: '',
      purpose: '',
      remarks: ''
    });
    setShowIssueModal(true);
  };

  // Handle Material Issue
  const handleIssueMaterial = async (e) => {
    e.preventDefault();
    
    if (!issueForm.quantity || parseFloat(issueForm.quantity) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid quantity' });
      return;
    }
    
    if (!issueForm.issuedTo) {
      setMessage({ type: 'error', text: 'Please enter who the material is issued to' });
      return;
    }
    
    if (parseFloat(issueForm.quantity) > selectedItem.availableQty) {
      setMessage({ type: 'error', text: `Insufficient stock. Available: ${selectedItem.availableQty} ${selectedItem.unit}` });
      return;
    }
    
    setIssuing(true);
    try {
      const response = await axios.post(`${API}/stores/material-issues`, {
        materialName: selectedItem.itemName,
        quantity: parseFloat(issueForm.quantity),
        unit: selectedItem.unit,
        issuedTo: issueForm.issuedTo,
        department: issueForm.department,
        purpose: issueForm.purpose,
        remarks: issueForm.remarks
      });
      
      setMessage({ type: 'success', text: `Issued ${issueForm.quantity} ${selectedItem.unit} of ${selectedItem.itemName}. Remaining: ${response.data.remainingStock.toFixed(2)} ${selectedItem.unit}` });
      setShowIssueModal(false);
      fetchStockData();
      fetchIssueHistory();
    } catch (error) {
      console.error('Error issuing material:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to issue material' });
    } finally {
      setIssuing(false);
    }
  };

  // Delete issue
  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm('Delete this issue record? This will restore the stock.')) return;
    
    try {
      await axios.delete(`${API}/stores/material-issues/${issueId}`);
      setMessage({ type: 'success', text: 'Issue deleted. Stock restored.' });
      fetchStockData();
      fetchIssueHistory();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete issue' });
    }
  };

  // Handle CSV Upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setMessage({ type: 'error', text: 'Please select a CSV file' });
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/stores/consumable-stock/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResult(response.data);
      setMessage({ 
        type: 'success', 
        text: `Upload complete: ${response.data.uploaded} items processed, ${response.data.skipped} skipped` 
      });
      fetchStockData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Upload failed';
      setMessage({ type: 'error', text: detail });
      setUploadResult({ errors: [detail] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/stores/consumable-stock/csv-template`, '_blank');
  };

  // Monthly Report
  const handleLoadMonthlyReport = async (monthOverride, yearOverride) => {
    const m = monthOverride ?? reportMonth;
    const y = yearOverride ?? reportYear;
    setLoadingReport(true);
    setShowReportModal(true);
    try {
      const res = await axios.get(`${API}/stores/consumable-stock/monthly-report?month=${m}&year=${y}`);
      setReportData(res.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load monthly report' });
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDownloadReport = () => {
    window.open(`${API}/stores/consumable-stock/monthly-report/download?month=${reportMonth}&year=${reportYear}`, '_blank');
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to DELETE ALL consumable stock data?\n\nThis will remove:\n- All stock inventory records\n- All stock lots (FIFO batches)\n- All material issue records\n\nThis action CANNOT be undone.')) return;
    if (!window.confirm('Final confirmation: This will permanently delete ALL consumable stock data. Continue?')) return;
    
    setDeletingAll(true);
    try {
      const response = await axios.delete(`${API}/stores/consumable-stock/delete-all`);
      setMessage({ type: 'success', text: `All stock data deleted. Stock: ${response.data.deleted.stockInventory}, Lots: ${response.data.deleted.stockLots}, Issues: ${response.data.deleted.materialIssues}` });
      fetchStockData();
      fetchIssueHistory();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Delete failed' });
    } finally {
      setDeletingAll(false);
    }
  };

  // Handle Stock Report Excel Upload
  const handleStockReportUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      setMessage({ type: 'error', text: 'Please select an Excel file (.xlsx)' });
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/stores/consumable-stock/import-stock-report`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResult(response.data);
      setMessage({ 
        type: 'success', 
        text: `Stock Report imported: ${response.data.inserted} new, ${response.data.updated} updated. Total value: ${response.data.totalValue?.toLocaleString()}` 
      });
      fetchStockData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Import failed';
      setMessage({ type: 'error', text: detail });
      setUploadResult({ errors: [detail] });
    } finally {
      setUploading(false);
      if (stockReportInputRef.current) stockReportInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
        <span className="ml-3 text-gray-600">Loading consumable stock from GRN...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="consumable-stock-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{pageLabel}</h1>
          <p className="text-gray-500 mt-1">Consumable stock inventory • Issue materials to deduct from stock</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`${baseRoute}/health`)}
            className={`relative border px-3 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 transition ${
              healthIssueCount > 0
                ? 'border-rose-300 bg-rose-100 text-rose-800 hover:bg-rose-200 ring-2 ring-rose-200 animate-pulse'
                : 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
            }`}
            data-testid="stock-health-btn"
            title={healthIssueCount > 0
              ? `${healthIssueCount} data-integrity issue(s) detected — click to review`
              : 'Find & fix orphan / drift stock data'}
          >
            <ShieldAlert size={16} />
            <span>Stock Health</span>
            {healthIssueCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold bg-rose-600 text-white rounded-full"
                    data-testid="stock-health-badge">
                {healthIssueCount}
              </span>
            )}
          </button>
          <button 
            onClick={() => handleLoadMonthlyReport()}
            className="hidden"
            data-testid="monthly-report-btn"
            aria-hidden
          >hidden</button>
          {/* Tools dropdown — Monthly Report / Compare Months / Export CSV /
              Bulk Upload merged into one clean menu. Stock Health stays as a
              standalone alert badge above. */}
          <div className="relative" data-testid="stock-tools-dropdown">
            <button
              onClick={() => setShowToolsMenu((v) => !v)}
              onBlur={() => setTimeout(() => setShowToolsMenu(false), 150)}
              className="bg-indigo-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 hover:bg-indigo-700"
              data-testid="stock-tools-btn"
            >
              <FileText size={18} />
              <span>Tools</span>
              <ChevronDown size={16} className={`transition-transform ${showToolsMenu ? 'rotate-180' : ''}`} />
            </button>
            {showToolsMenu && (
              <div
                className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden"
                data-testid="stock-tools-menu"
              >
                <button
                  onClick={() => { setShowToolsMenu(false); handleLoadMonthlyReport(); }}
                  className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-emerald-50 text-gray-700 border-b border-gray-100"
                  data-testid="tools-monthly-report"
                >
                  <FileText size={16} className="text-emerald-600" />
                  <span>Monthly Report</span>
                </button>
                <button
                  onClick={() => { setShowToolsMenu(false); openCompareModal(); }}
                  className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-violet-50 text-gray-700 border-b border-gray-100"
                  data-testid="tools-compare-months"
                >
                  <Scale size={16} className="text-violet-600" />
                  <span>Compare Months</span>
                </button>
                <button
                  onClick={() => { setShowToolsMenu(false); handleExportCsv(); }}
                  disabled={!filteredData.length}
                  className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-blue-50 text-gray-700 border-b border-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="tools-export-csv"
                  title={!filteredData.length ? 'No rows to export' : 'Download CSV (Bulk / GRN / Issued breakdown)'}
                >
                  <Download size={16} className="text-blue-600" />
                  <span>Export CSV</span>
                </button>
                <button
                  onClick={() => { setShowToolsMenu(false); setShowUploadModal(true); }}
                  className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-amber-50 text-gray-700"
                  data-testid="tools-bulk-upload"
                >
                  <Upload size={16} className="text-amber-600" />
                  <span>Bulk Upload</span>
                </button>
              </div>
            )}
          </div>
          <button 
            onClick={fetchStockData}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 hover:bg-gray-50"
          >
            <RefreshCw size={18} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'} border rounded-xl p-4`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right"><X size={16} /></button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Items</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Package className="text-blue-500" size={28} />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">In Stock</p>
              <p className="text-2xl font-bold text-green-600">{stats.inStock}</p>
            </div>
            <CheckCircle className="text-green-500" size={28} />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Low/Out of Stock</p>
              <p className="text-2xl font-bold text-red-600">{stats.lowStock}</p>
            </div>
            <AlertTriangle className="text-red-500" size={28} />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Value</p>
              <p className="text-2xl font-bold text-purple-600">₹{stats.totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
            </div>
            <FileText className="text-purple-500" size={28} />
          </div>
        </div>
      </div>

      {stats.phantom > 0 && (
        <div className="bg-rose-50 border-l-4 border-rose-500 rounded-lg p-4 flex items-start gap-3" data-testid="phantom-alert-banner">
          <AlertTriangle className="text-rose-600 flex-shrink-0 mt-0.5" size={22} />
          <div className="flex-1">
            <p className="font-semibold text-rose-900">
              {stats.phantom} phantom-consumption row{stats.phantom === 1 ? '' : 's'} detected
            </p>
            <p className="text-sm text-rose-800 mt-1">
              {stats.phantom === 1 ? 'This item has' : 'These items have'} issuances recorded against {stats.phantom === 1 ? 'a' : ''} commodity {stats.phantom === 1 ? 'name' : 'names'} with <strong>zero receipts</strong>. Two common causes:
              <span className="block mt-1">
                <strong>(a)</strong> a spelling variant — the GRN was filed under one name and the issuance under another → use <em>Relabel</em>;{' '}
                <strong>(b)</strong> service / one-off job that never went through a GRN → use <em>Mark as Service</em>.
              </span>
            </p>
            <div className="flex flex-wrap gap-3 mt-2 items-center">
              <button
                onClick={() => setStatusFilter('phantom-only')}
                className="inline-flex items-center gap-1 text-sm font-medium text-rose-700 hover:text-rose-900 hover:underline"
                data-testid="phantom-banner-show-rows"
              >
                → Show me the {stats.phantom} phantom row{stats.phantom === 1 ? '' : 's'} below
              </button>
              <button
                onClick={async () => {
                  const phantomItems = stockData.filter(i => i.isPhantomConsumption);
                  if (phantomItems.length === 0) return;
                  if (!window.confirm(
                    `MARK ALL ${phantomItems.length} phantom row${phantomItems.length === 1 ? '' : 's'} as Service / One-Off Job?\n\n` +
                    phantomItems.map(p => `• ${p.itemName} — ${p.consumedQty || 0} ${p.unit || 'NOS'}`).join('\n') +
                    `\n\nFor each row, a synthetic receipt equal to consumed qty will be stamped so the books balance. ` +
                    `Use this if these are service items that never went through GRN flow. Audit-logged.`
                  )) return;
                  const results = { ok: 0, fail: [] };
                  for (const p of phantomItems) {
                    try {
                      await axios.post(`${API}/stores/phantom-issues/mark-service`, {
                        itemName: p.itemName,
                        unit: p.unit || 'NOS',
                      });
                      results.ok += 1;
                    } catch (err) {
                      results.fail.push(`${p.itemName}: ${err?.response?.data?.detail || 'error'}`);
                    }
                  }
                  alert(
                    `Done — ${results.ok}/${phantomItems.length} marked as service.` +
                    (results.fail.length ? `\n\nFailures:\n${results.fail.join('\n')}` : '')
                  );
                  fetchStockData();
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded text-xs font-semibold hover:bg-emerald-700"
                data-testid="phantom-banner-mark-all-service"
                title="One-click: stamp synthetic receipts for every phantom row so the books balance"
              >
                ✓ Mark ALL {stats.phantom} as Service (one-click fix)
              </button>
              <a href="/settings/data-integrity"
                 className="inline-flex items-center gap-1 text-sm font-medium text-rose-700 hover:text-rose-900 hover:underline"
                 data-testid="phantom-banner-fix-link">
                → Resolve in Data Integrity → Phantom Issuance Records
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit" data-testid="stock-tabs">
        <button
          onClick={() => setActiveTab('stock')}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition ${activeTab === 'stock' ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-stock"
        >
          Stock Inventory
        </button>
        <button
          onClick={() => setActiveTab('consumption')}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition ${activeTab === 'consumption' ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-consumption"
        >
          Consumption
        </button>
      </div>

      {activeTab === 'stock' && (<>
      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="all">All Status</option>
            <option value="In Stock">In Stock</option>
            <option value="Low Stock">Low Stock</option>
            <option value="Out of Stock">Out of Stock</option>
          </select>
          <select
            value={partyFilter}
            onChange={(e) => setPartyFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="all">All Parties</option>
            {uniqueParties.map(party => (
              <option key={party} value={party}>{party}</option>
            ))}
          </select>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Stock Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-semibold text-gray-900">{pageLabel} Items ({filteredData.length})</h3>
        </div>
        <div className="overflow-x-auto">
          {filteredData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="mx-auto mb-3 text-gray-300" size={48} />
              <p className="font-medium">No stock items found</p>
              <p className="text-sm">Items will appear here once GRN is approved through QA</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                <tr>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Sources</th>
                  <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase">UOM</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase">Received</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase">Consumed</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase">Available</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase">Rate</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase">Value</th>
                  <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Last GRN</th>
                  <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredData.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                          item.isPhantomConsumption ? 'bg-rose-50 border-l-4 border-rose-500'
                          : expandedItem === item.id ? 'bg-amber-50' : ''
                        }`}
                        onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                        data-testid={`stock-row-${item.id}`}>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                            <ChevronDown size={14} className={`transition-transform ${expandedItem === item.id ? 'rotate-180' : ''}`} />
                            {item.itemName}
                            {item.lotCount > 0 && (
                              <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">{item.activeLotCount || item.lotCount} lots</span>
                            )}
                            {item.isPhantomConsumption && (
                              <span className="text-[10px] bg-rose-600 text-white px-1.5 py-0.5 rounded font-semibold uppercase" title="Issues exist for this name but zero receipts. Either a spelling variant (use Relabel) or service work that never went through GRN (use Mark as Service).">
                                ⚠ Phantom
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 ml-5">
                            {item.hsnCode && <span className="text-blue-600 mr-2">HSN: {item.hsnCode}</span>}
                            {item.party}
                          </p>
                          {item.isPhantomConsumption && (
                            <div className="ml-5 mt-2 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={async () => {
                                  const target = window.prompt(
                                    `RELABEL all issuances of "${item.itemName}" to which existing commodity?\n\n` +
                                    `Type the correctly-spelled commodity name (must already exist in your stock).`,
                                    ''
                                  );
                                  if (!target) return;
                                  try {
                                    const r = await axios.post(`${API}/stores/phantom-issues/relabel`, {
                                      fromName: item.itemName,
                                      toName: target,
                                    });
                                    alert(r.data?.message || 'Relabelled');
                                    fetchStockData();
                                  } catch (err) {
                                    alert(err?.response?.data?.detail || 'Relabel failed');
                                  }
                                }}
                                className="px-3 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 font-medium"
                                data-testid={`phantom-relabel-${item.id}`}
                                title="Rename every issuance under this name to a correctly-spelled commodity that already exists"
                              >
                                Relabel →
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(
                                    `DELETE all issuance records for "${item.itemName}"?\n\n` +
                                    `Consumed: ${item.consumedQty || 0} ${item.unit || 'NOS'}\n\n` +
                                    `Use this ONLY if the issuances are bogus / duplicates / test entries / wrong-commodity. ` +
                                    `Action is audit-logged.`
                                  )) return;
                                  try {
                                    // Pass the canonical normalized key (backend does its own normalize)
                                    const r = await axios.post(`${API}/stores/phantom-issues/delete`, {
                                      normalizedKey: (item.itemName || '').toUpperCase().trim(),
                                    });
                                    if ((r.data?.deleted || 0) === 0) {
                                      // Fallback — name may not normalize-match; pass the raw name as ids lookup
                                      alert('No matching rows found by normalized key. Try Relabel or open Data Integrity for advanced control.');
                                    } else {
                                      alert(r.data?.message || 'Deleted');
                                    }
                                    fetchStockData();
                                  } catch (err) {
                                    alert(err?.response?.data?.detail || 'Delete failed');
                                  }
                                }}
                                className="px-3 py-1 bg-rose-600 text-white rounded text-xs hover:bg-rose-700 font-medium"
                                data-testid={`phantom-delete-${item.id}`}
                                title="Hard-delete all issuance records under this name"
                              >
                                Delete Issues
                              </button>
                              <button
                                onClick={async () => {
                                  // Quick fix for services / one-off jobs that were issued but never
                                  // had a GRN: stamp a synthetic receipt equal to consumed qty so the
                                  // numbers balance and the row drops out of phantom state.
                                  if (!window.confirm(
                                    `MARK "${item.itemName}" as a Service / One-Off Job?\n\n` +
                                    `This stamps a synthetic receipt equal to the consumed quantity (${item.consumedQty || 0} ${item.unit || 'NOS'}) ` +
                                    `so the row balances and disappears from the phantom warning. ` +
                                    `Use for service work that doesn't follow the standard PO→GRN→issue flow.`
                                  )) return;
                                  try {
                                    const r = await axios.post(`${API}/stores/phantom-issues/mark-service`, {
                                      itemName: item.itemName,
                                      unit: item.unit || 'NOS',
                                    });
                                    alert(r.data?.message || 'Marked as service');
                                    fetchStockData();
                                  } catch (err) {
                                    alert(err?.response?.data?.detail || 'Mark as service failed');
                                  }
                                }}
                                className="px-3 py-1 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700 font-medium"
                                data-testid={`phantom-mark-service-${item.id}`}
                                title="Treat as service work (creates a synthetic receipt so numbers balance)"
                              >
                                Mark as Service ✓
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs whitespace-nowrap" data-testid={`stock-sources-${item.id}`}>
                        {(() => {
                          const bd = item.breakdown || {};
                          const bulk = bd.bulkBaseline || 0;
                          const lot = bd.lotReceived || 0;
                          const cons = bd.totalConsumed || 0;
                          const hasBulk = bulk > 0.001;
                          const hasLot = lot > 0.001;
                          const hasIssued = cons > 0.001;
                          if (!hasBulk && !hasLot && !hasIssued) {
                            return <span className="text-gray-400 italic">—</span>;
                          }
                          return (
                            <div className="space-y-0.5">
                              {hasBulk && (
                                <div className="flex items-center gap-1" title="Bulk-uploaded opening baseline (no underlying GRN)">
                                  <span className="inline-block w-2 h-2 bg-amber-400 rounded-full" />
                                  <span className="text-amber-800 font-medium">Bulk +{bulk.toFixed(2)}</span>
                                </div>
                              )}
                              {hasLot && (
                                <div className="flex items-center gap-1" title="Sum of receivedQty across all GRN-approved lots">
                                  <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full" />
                                  <span className="text-emerald-800 font-medium">GRN +{lot.toFixed(2)}</span>
                                </div>
                              )}
                              {hasIssued && (
                                <div className="flex items-center gap-1" title="Total quantity issued via Material Issues">
                                  <span className="inline-block w-2 h-2 bg-rose-500 rounded-full" />
                                  <span className="text-rose-700 font-medium">Issued −{cons.toFixed(2)}</span>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-4 text-center text-sm text-gray-700 font-medium">
                        {item.unit || '-'}
                      </td>
                      <td className="px-4 py-4 text-right text-sm text-gray-600">
                        {(item.quantity || 0).toFixed(2)} {item.unit}
                      </td>
                      <td className="px-4 py-4 text-right text-sm text-orange-600 font-medium">
                        {(item.consumedQty || 0).toFixed(2)} {item.unit}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="font-bold text-green-700">{(item.availableQty || 0).toFixed(2)}</span>
                        <span className="text-gray-500 text-sm ml-1">{item.unit}</span>
                      </td>
                      <td className="px-4 py-4 text-right text-sm text-gray-600">
                        <div>
                          <span>₹{(item.weightedAvgRate || item.rate || 0).toFixed(2)}</span>
                          {item.lotCount > 1 && <p className="text-[10px] text-gray-400">Wtd. Avg</p>}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right text-sm font-medium text-purple-600">
                        ₹{(item.totalValue || item.value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                          {getStatusIcon(item.status)}
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {item.lastGRN}
                      </td>
                      <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => openIssueModal(item)}
                          disabled={item.availableQty <= 0}
                          className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 mx-auto"
                          title="Issue Material (FIFO)"
                          data-testid={`issue-btn-${item.id}`}
                        >
                          <Minus size={14} />
                          Issue
                        </button>
                      </td>
                    </tr>
                    {/* Expandable Lot Details */}
                    {expandedItem === item.id && (
                      item.lots && item.lots.length > 0 ? (
                        <tr>
                          <td colSpan={10} className="px-4 py-0 bg-amber-50/50">
                            <div className="py-3 px-4">
                              <p className="text-xs font-semibold text-gray-600 mb-2">
                                STOCK BATCHES (FIFO Order - Oldest First) &bull; Weighted Avg Rate: ₹{(item.weightedAvgRate || 0).toFixed(2)}
                              </p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500 border-b border-gray-200">
                                  <th className="py-1.5 text-left font-medium">GRN / Source</th>
                                  <th className="py-1.5 text-left font-medium">Date</th>
                                  <th className="py-1.5 text-left font-medium">Vendor</th>
                                  <th className="py-1.5 text-right font-medium">Received</th>
                                  <th className="py-1.5 text-right font-medium">Remaining</th>
                                  <th className="py-1.5 text-right font-medium">Rate</th>
                                  <th className="py-1.5 text-right font-medium">Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.lots.map((lot, idx) => (
                                  <tr key={lot.lotId || idx} className={`border-b border-gray-100 ${lot.remainingQty <= 0 ? 'opacity-40' : ''}`}>
                                    <td className="py-1.5 text-gray-700 font-mono">{lot.grnNumber}</td>
                                    <td className="py-1.5 text-gray-600">{lot.grnDate}</td>
                                    <td className="py-1.5 text-gray-600">{lot.vendorName || '-'}</td>
                                    <td className="py-1.5 text-right text-gray-600">{lot.receivedQty?.toFixed(2)}</td>
                                    <td className="py-1.5 text-right font-semibold text-green-700">{lot.remainingQty?.toFixed(2)}</td>
                                    <td className="py-1.5 text-right text-gray-700">₹{lot.rate?.toFixed(2)}</td>
                                    <td className="py-1.5 text-right text-purple-600">₹{(lot.value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="font-semibold text-gray-800 border-t-2 border-gray-300">
                                  <td colSpan={3} className="py-2">TOTAL ({item.lots.filter(l => l.remainingQty > 0).length} active lots)</td>
                                  <td className="py-2 text-right">{(item.quantity || 0).toFixed(2)}</td>
                                  <td className="py-2 text-right text-green-700">{(item.availableQty || 0).toFixed(2)}</td>
                                  <td className="py-2 text-right">₹{(item.weightedAvgRate || 0).toFixed(2)}</td>
                                  <td className="py-2 text-right text-purple-600">₹{(item.totalValue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </td>
                      </tr>
                      ) : (
                        <tr data-testid={`no-lots-message-${item.id}`}>
                          <td colSpan={10} className="px-4 py-0 bg-gray-50/70">
                            <div className="py-4 px-4 text-center text-xs text-gray-600">
                              <p className="font-semibold text-gray-700 mb-1">No GRN history attached to this stock</p>
                              <p>
                                <span className="font-semibold">{(item.quantity || 0).toFixed(2)} {item.unit}</span> were recorded directly to inventory
                                — likely added via the <em>bulk CSV upload</em> or as an opening-balance entry.
                                {(item.consumedQty || 0) > 0 && <> All <span className="font-semibold">{(item.consumedQty || 0).toFixed(2)} {item.unit}</span> have been issued.</>}
                              </p>
                              <p className="text-gray-500 mt-1">
                                To attach a GRN going forward, create a fresh GRN against the relevant PO — future receipts will show their lot history here.
                              </p>
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recent Issues Table */}
      {issueHistory.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-900">Recent Material Issues</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Issue ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Material</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Quantity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Issued To</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  {canDelete && <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {issueHistory.slice(0, 10).map((issue) => (
                  <tr key={issue.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{issue.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{issue.materialName}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-orange-600">
                      {issue.quantity} {issue.unit}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{issue.issuedTo}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{issue.department || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{issue.issueDate}</td>
                    {canDelete && (
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDeleteIssue(issue.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          title="Delete (Restore Stock)"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-blue-800 text-sm">
          <strong>Note:</strong> Stock is automatically populated from QA-approved GRN items. 
          Use the "Issue" button to deduct materials from stock when issuing to departments or personnel.
          The consumed quantity will be tracked and deducted from available stock.
        </p>
      </div>

      </>)}

      {/* Consumption Tab */}
      {activeTab === 'consumption' && (
        <div className="space-y-4">
          {/* Period Filters */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex gap-2" data-testid="consumption-period-filters">
                {[
                  { key: 'week', label: 'Week' },
                  { key: 'month', label: 'Month' },
                  { key: 'quarter', label: 'Quarter' },
                  { key: 'year', label: 'Year' },
                ].map(p => (
                  <button
                    key={p.key}
                    onClick={() => setConsumptionPeriod(p.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${consumptionPeriod === p.key ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    data-testid={`period-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {filteredConsumptionData && (
                <div className="text-sm text-gray-500">
                  {filteredConsumptionData.periodLabel} &bull; {filteredConsumptionData.totalIssues || 0} issues{deptFilter !== 'all' ? ` (${deptFilter})` : ''}
                </div>
              )}
            </div>
            {/* Search + Department Filter */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[240px] max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={consumptionSearch}
                  onChange={(e) => setConsumptionSearch(e.target.value)}
                  placeholder="Search by commodity name…"
                  className="w-full pl-9 pr-9 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-100 focus:border-amber-300 outline-none bg-white"
                  data-testid="consumption-search"
                />
                {consumptionSearch && (
                  <button
                    onClick={() => setConsumptionSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    data-testid="consumption-search-clear"
                    title="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <select
                value={deptFilter}
                onChange={(e) => setDeptFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-100 bg-white"
                data-testid="consumption-dept-filter"
              >
                <option value="all">All Departments</option>
                <option value="Production">Production</option>
                <option value="Raw Material">Raw Material</option>
                <option value="Dispatch">Dispatch</option>
                <option value="Stores">Stores</option>
                <option value="Projects">Projects</option>
                <option value="Maintenance">Maintenance</option>
                <option value="HR">HR</option>
                <option value="Imports">Imports</option>
                <option value="Accounts and Office">Accounts and Office</option>
              </select>
            </div>
          </div>

          {/* Consumption Summary Cards */}
          {filteredConsumptionData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <p className="text-sm text-gray-500">Total Commodities</p>
                <p className="text-2xl font-bold text-gray-900">{filteredConsumptionData.totalCommodities || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <p className="text-sm text-gray-500">Total Issues</p>
                <p className="text-2xl font-bold text-blue-600">{filteredConsumptionData.totalIssues || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <p className="text-sm text-gray-500">Total Qty Consumed</p>
                <p className="text-2xl font-bold text-orange-600">{(filteredConsumptionData.grandTotalQty || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <p className="text-sm text-gray-500">Total Value Consumed</p>
                <p className="text-2xl font-bold text-purple-600" data-testid="consumption-total-value">₹{(filteredConsumptionData.grandTotalValue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
              </div>
            </div>
          )}

          {/* Consumption Table */}
          {loadingConsumption ? (
            <div className="bg-white rounded-xl p-12 border border-gray-200 flex items-center justify-center">
              <RefreshCw size={24} className="animate-spin text-amber-500" />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <table className="min-w-full" data-testid="consumption-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Commodity</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Issues</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Avg Rate</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Value</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Departments</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(filteredConsumptionData?.data || []).map((item, idx) => (
                    <React.Fragment key={idx}>
                      <tr
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${expandedItem === `cons-${idx}` ? 'bg-amber-50' : ''}`}
                        onClick={() => setExpandedItem(expandedItem === `cons-${idx}` ? null : `cons-${idx}`)}
                        data-testid={`consumption-row-${idx}`}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <ChevronDown size={14} className={`transition-transform text-gray-400 ${expandedItem === `cons-${idx}` ? 'rotate-180' : ''}`} />
                            <span className="font-medium text-gray-900">{item.commodityName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-gray-600">{item.issueCount}</td>
                        <td className="px-4 py-4 text-right text-sm font-medium text-orange-600">
                          {item.totalQty.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-gray-600">
                          {item.rateMissing
                            ? <span className="text-amber-600 italic" title="No rate recorded in stock_inventory or any lot for this material. Update its rate via Bulk Upload to see the value."><AlertCircle size={11} className="inline mr-0.5" />no rate</span>
                            : `₹${item.avgRate.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                        </td>
                        <td className="px-4 py-4 text-right text-sm font-semibold text-purple-600">
                          {item.rateMissing
                            ? <span className="text-amber-600 italic">—</span>
                            : `₹${item.totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="flex flex-wrap gap-1">
                            {item.departments.map((d, i) => (
                              <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs">{d}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded issue details */}
                      {expandedItem === `cons-${idx}` && item.issues && (
                        <tr>
                          <td colSpan={6} className="px-4 py-0 bg-amber-50/50">
                            <div className="py-3 px-4">
                              <p className="text-xs font-semibold text-gray-600 mb-2">ISSUE DETAILS</p>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500 border-b border-gray-200">
                                    <th className="py-1.5 text-left font-medium">Issue #</th>
                                    <th className="py-1.5 text-left font-medium">Date</th>
                                    <th className="py-1.5 text-left font-medium">Issued To</th>
                                    <th className="py-1.5 text-left font-medium">Department</th>
                                    <th className="py-1.5 text-left font-medium">Purpose</th>
                                    <th className="py-1.5 text-right font-medium">Qty</th>
                                    <th className="py-1.5 text-right font-medium">Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.issues.map((iss, iIdx) => (
                                    <tr key={iIdx} className="border-b border-gray-100">
                                      <td className="py-1.5 text-gray-700 font-mono">{iss.id}</td>
                                      <td className="py-1.5 text-gray-600">{iss.date}</td>
                                      <td className="py-1.5 text-gray-600">{iss.issuedTo}</td>
                                      <td className="py-1.5 text-gray-600">{iss.department}</td>
                                      <td className="py-1.5 text-gray-600">{iss.purpose}</td>
                                      <td className="py-1.5 text-right font-medium">{iss.quantity} {iss.unit}</td>
                                      <td className="py-1.5 text-right text-purple-600">₹{(iss.value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr className="font-semibold text-gray-800 border-t-2 border-gray-300">
                                    <td colSpan={5} className="py-2">TOTAL</td>
                                    <td className="py-2 text-right">{item.totalQty.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                                    <td className="py-2 text-right text-purple-600">₹{item.totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {(filteredConsumptionData?.data || []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                        No consumption data for the selected period
                      </td>
                    </tr>
                  )}
                </tbody>
                {(filteredConsumptionData?.data || []).length > 0 && (
                  <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                    <tr className="font-bold">
                      <td className="px-4 py-3 text-gray-800">GRAND TOTAL</td>
                      <td className="px-4 py-3 text-right text-gray-800">{filteredConsumptionData?.totalIssues || 0}</td>
                      <td className="px-4 py-3 text-right text-orange-600">{(filteredConsumptionData?.grandTotalQty || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                      <td className="px-4 py-3 text-right text-gray-600">-</td>
                      <td className="px-4 py-3 text-right text-purple-600">₹{(filteredConsumptionData?.grandTotalValue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                      <td className="px-4 py-3"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      )}

      {/* Compare Months Modal */}
      {showCompareModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-6">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowCompareModal(false)}></div>
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col" data-testid="compare-months-modal">
              <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-violet-600 to-purple-700 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Scale size={20} />
                  <h2 className="text-lg font-bold">Compare Two Months</h2>
                </div>
                <button onClick={() => setShowCompareModal(false)} className="p-2 text-white/80 hover:bg-white/10 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              {/* Pickers */}
              <div className="p-4 border-b border-gray-200 bg-violet-50/40 flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700 block">Base month</label>
                  <div className="flex items-center gap-1 mt-1">
                    <select value={baseMonth} onChange={(e) => setBaseMonth(Number(e.target.value))}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                            data-testid="compare-base-month">
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                        <option key={m} value={i + 1}>{m}</option>
                      ))}
                    </select>
                    <select value={baseYear} onChange={(e) => setBaseYear(Number(e.target.value))}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                            data-testid="compare-base-year">
                      {Array.from({ length: 7 }, (_, i) => _now.getFullYear() - 5 + i).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <span className="text-2xl text-violet-500 leading-none pb-1">↔</span>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block">Compare month</label>
                  <div className="flex items-center gap-1 mt-1">
                    <select value={cmpMonth} onChange={(e) => setCmpMonth(Number(e.target.value))}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                            data-testid="compare-cmp-month">
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                        <option key={m} value={i + 1}>{m}</option>
                      ))}
                    </select>
                    <select value={cmpYear} onChange={(e) => setCmpYear(Number(e.target.value))}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                            data-testid="compare-cmp-year">
                      {Array.from({ length: 7 }, (_, i) => _now.getFullYear() - 5 + i).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button onClick={handleLoadCompare} disabled={compareLoading}
                        className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 flex items-center gap-2 disabled:opacity-60"
                        data-testid="compare-load-btn">
                  {compareLoading ? <Loader2 size={14} className="animate-spin" /> : <Scale size={14} />}
                  {compareLoading ? 'Loading…' : 'Compare'}
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-5">
                {compareLoading && <div className="text-center text-gray-500 py-12">Loading comparison…</div>}
                {!compareLoading && compareData && (
                  <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-3 gap-3 mb-5">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Purchase value Δ</p>
                        <p className={`text-lg font-bold ${compareData.totals.purchaseValueDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {compareData.totals.purchaseValueDelta >= 0 ? '+' : ''}₹{compareData.totals.purchaseValueDelta.toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Consumption value Δ</p>
                        <p className={`text-lg font-bold ${compareData.totals.consumptionValueDelta >= 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                          {compareData.totals.consumptionValueDelta >= 0 ? '+' : ''}₹{compareData.totals.consumptionValueDelta.toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Closing value Δ</p>
                        <p className={`text-lg font-bold ${compareData.totals.closingValueDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {compareData.totals.closingValueDelta >= 0 ? '+' : ''}₹{compareData.totals.closingValueDelta.toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>

                    <p className="text-sm text-gray-700 mb-2">
                      <strong className="text-violet-700">{compareData.movers}</strong> of {compareData.totalItems} items moved between <strong>{compareData.base.label}</strong> and <strong>{compareData.compare.label}</strong>. Sorted by absolute closing change.
                    </p>

                    {/* Table */}
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr className="text-xs text-gray-600 uppercase">
                            <th className="px-3 py-2 text-left">Item</th>
                            <th className="px-3 py-2 text-right" colSpan={4}>{compareData.base.label}</th>
                            <th className="px-3 py-2 text-right border-l border-gray-200" colSpan={4}>{compareData.compare.label}</th>
                            <th className="px-3 py-2 text-right border-l border-gray-200" colSpan={3}>Δ</th>
                          </tr>
                          <tr className="text-xs text-gray-500 bg-gray-100">
                            <th className="px-3 py-1.5"></th>
                            <th className="px-2 py-1.5 text-right">Open</th>
                            <th className="px-2 py-1.5 text-right">Pur</th>
                            <th className="px-2 py-1.5 text-right">Cons</th>
                            <th className="px-2 py-1.5 text-right">Close</th>
                            <th className="px-2 py-1.5 text-right border-l">Open</th>
                            <th className="px-2 py-1.5 text-right">Pur</th>
                            <th className="px-2 py-1.5 text-right">Cons</th>
                            <th className="px-2 py-1.5 text-right">Close</th>
                            <th className="px-2 py-1.5 text-right border-l">Pur</th>
                            <th className="px-2 py-1.5 text-right">Cons</th>
                            <th className="px-2 py-1.5 text-right">Close</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {compareData.items.map((r, i) => {
                            const closingClass = r.delta.closing > 0.001 ? 'text-emerald-700 font-semibold'
                                                : r.delta.closing < -0.001 ? 'text-rose-700 font-semibold' : 'text-gray-400';
                            const consClass = r.delta.consumption > 0.001 ? 'text-rose-700 font-medium'
                                              : r.delta.consumption < -0.001 ? 'text-emerald-700 font-medium' : 'text-gray-400';
                            const purClass = r.delta.purchase > 0.001 ? 'text-emerald-700 font-medium'
                                             : r.delta.purchase < -0.001 ? 'text-rose-700 font-medium' : 'text-gray-400';
                            const fmt = (v) => Math.abs(v) < 0.001 ? '—' : v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
                            const sign = (v) => Math.abs(v) < 0.001 ? '' : (v > 0 ? '+' : '');
                            return (
                              <tr key={i} className="hover:bg-violet-50/30" data-testid={`compare-row-${i}`}>
                                <td className="px-3 py-1.5 font-medium text-gray-900">{r.itemName} <span className="text-xs text-gray-400">{r.unit}</span></td>
                                <td className="px-2 py-1.5 text-right text-gray-700">{fmt(r.base.opening)}</td>
                                <td className="px-2 py-1.5 text-right text-gray-700">{fmt(r.base.purchase)}</td>
                                <td className="px-2 py-1.5 text-right text-gray-700">{fmt(r.base.consumption)}</td>
                                <td className="px-2 py-1.5 text-right text-gray-700">{fmt(r.base.closing)}</td>
                                <td className="px-2 py-1.5 text-right text-gray-700 border-l">{fmt(r.compare.opening)}</td>
                                <td className="px-2 py-1.5 text-right text-gray-700">{fmt(r.compare.purchase)}</td>
                                <td className="px-2 py-1.5 text-right text-gray-700">{fmt(r.compare.consumption)}</td>
                                <td className="px-2 py-1.5 text-right text-gray-700">{fmt(r.compare.closing)}</td>
                                <td className={`px-2 py-1.5 text-right border-l ${purClass}`}>{sign(r.delta.purchase)}{fmt(r.delta.purchase)}</td>
                                <td className={`px-2 py-1.5 text-right ${consClass}`}>{sign(r.delta.consumption)}{fmt(r.delta.consumption)}</td>
                                <td className={`px-2 py-1.5 text-right ${closingClass}`}>{sign(r.delta.closing)}{fmt(r.delta.closing)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      <span className="inline-block w-3 h-3 bg-emerald-500 rounded-sm mr-1 align-middle"></span> closing/purchase up
                      <span className="inline-block w-3 h-3 bg-rose-500 rounded-sm mx-1 ml-3 align-middle"></span> closing/purchase down (or consumption up)
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => { setShowUploadModal(false); setUploadResult(null); }}></div>
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" data-testid="bulk-upload-modal">
              <button 
                onClick={() => { setShowUploadModal(false); setUploadResult(null); }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
              
              <h2 className="text-xl font-bold text-gray-900 mb-4">Import Stock Data</h2>
              
              {/* Upload Mode Tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => { setUploadMode('stock-report'); setUploadResult(null); }}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    uploadMode === 'stock-report' 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="tab-stock-report"
                >
                  Stock Report (Excel)
                </button>
                <button
                  onClick={() => { setUploadMode('csv'); setUploadResult(null); }}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    uploadMode === 'csv' 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="tab-csv-upload"
                >
                  CSV Upload
                </button>
              </div>
              
              {/* Stock Report Mode */}
              {uploadMode === 'stock-report' && (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-amber-800">March Stock Report Import</p>
                    <p className="text-xs text-amber-600 mt-1">
                      Upload the monthly stock report Excel file. March closing stock becomes April FY opening. HSN codes will be stored for GST.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs text-gray-600">
                    <p className="font-medium text-gray-700 mb-1">Expected Excel columns:</p>
                    <p>SL.NO | <strong>ITEM NAME</strong> | <strong>HSN CODE</strong> | OPENING | PURCHASE | CONSUMPTION | <strong>CL STOCK</strong> | UNIT | RATE</p>
                  </div>
                  
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-amber-400 transition-colors cursor-pointer mb-4"
                    onClick={() => stockReportInputRef.current?.click()}
                    data-testid="stock-report-dropzone"
                  >
                    <input
                      ref={stockReportInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleStockReportUpload}
                      className="hidden"
                      data-testid="stock-report-input"
                    />
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 size={32} className="text-amber-500 animate-spin mb-2" />
                        <p className="text-sm text-gray-600">Importing stock report...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload size={32} className="text-amber-400 mb-2" />
                        <p className="text-sm font-medium text-gray-700">Click to select Excel file (.xlsx)</p>
                        <p className="text-xs text-gray-500 mt-1">Stock Report March 2026</p>
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {/* CSV Mode */}
              {uploadMode === 'csv' && (
                <>
                  <p className="text-sm text-gray-500 mb-4">Upload a CSV file to add stock items in bulk.</p>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-800">CSV Template</p>
                        <p className="text-xs text-blue-600 mt-0.5">Download, fill, and upload</p>
                      </div>
                      <button
                        onClick={handleDownloadTemplate}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm flex items-center gap-1.5 hover:bg-blue-700"
                        data-testid="download-template-btn"
                      >
                        <Download size={14} />
                        Download
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs text-gray-600">
                    <p className="font-medium text-gray-700 mb-1">Required columns:</p>
                    <p><strong>itemName</strong> - Material name | <strong>quantity</strong> - Stock quantity</p>
                    <p className="mt-1 text-gray-500">Optional: category, unit, rate, vendor, invoiceNo, grnNo, remarks</p>
                  </div>
                  
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-amber-400 transition-colors cursor-pointer mb-4"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-dropzone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="file-input"
                    />
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 size={32} className="text-amber-500 animate-spin mb-2" />
                        <p className="text-sm text-gray-600">Processing upload...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload size={32} className="text-gray-400 mb-2" />
                        <p className="text-sm font-medium text-gray-700">Click to select CSV file</p>
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {/* Upload Result */}
              {uploadResult && (
                <div className="mt-4 space-y-3" data-testid="upload-result">
                  <div className={`rounded-lg p-3 ${
                    uploadResult.errors?.length > 0 && !uploadResult.uploaded && !uploadResult.inserted 
                      ? 'bg-red-50 border border-red-200' 
                      : 'bg-green-50 border border-green-200'
                  }`}>
                    <p className="text-sm font-medium">
                      {(uploadResult.uploaded > 0 || uploadResult.inserted > 0 || uploadResult.updated > 0) ? (
                        <span className="text-green-800">
                          {uploadResult.message || `Processed successfully`}
                        </span>
                      ) : (
                        <span className="text-red-800">Upload had issues</span>
                      )}
                    </p>
                    {uploadResult.totalValue > 0 && (
                      <p className="text-xs text-green-700 mt-1">Total stock value: Rs. {uploadResult.totalValue?.toLocaleString()}</p>
                    )}
                    {uploadResult.inStock > 0 && (
                      <p className="text-xs text-green-600 mt-0.5">{uploadResult.inStock} items in stock, {uploadResult.zeroStock} out of stock</p>
                    )}
                  </div>
                  
                  {/* Show items preview */}
                  {uploadResult.items?.length > 0 && (
                    <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Item</th>
                            {uploadResult.items[0]?.closing !== undefined ? (
                              <>
                                <th className="px-3 py-2 text-right">Closing</th>
                                <th className="px-3 py-2 text-left">HSN</th>
                              </>
                            ) : (
                              <>
                                <th className="px-3 py-2 text-right">Qty</th>
                                <th className="px-3 py-2 text-center">Action</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {uploadResult.items.map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-1.5">{item.itemName || item.name}</td>
                              {item.closing !== undefined ? (
                                <>
                                  <td className="px-3 py-1.5 text-right">{item.closing} {item.unit}</td>
                                  <td className="px-3 py-1.5 text-gray-500">{item.hsn}</td>
                                </>
                              ) : (
                                <>
                                  <td className="px-3 py-1.5 text-right">{item.quantity}</td>
                                  <td className="px-3 py-1.5 text-center">
                                    <span className={`px-1.5 py-0.5 rounded text-xs ${item.action === 'created' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                      {item.action}
                                    </span>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {uploadResult.errors?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-amber-800 mb-1">Warnings:</p>
                      {uploadResult.errors.map((err, idx) => (
                        <p key={idx} className="text-xs text-amber-700">{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setShowUploadModal(false); setUploadResult(null); }}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowReportModal(false)}></div>
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto" data-testid="monthly-report-modal">
              <button 
                onClick={() => setShowReportModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
              
              {loadingReport ? (
                <div className="flex flex-col items-center py-12">
                  <Loader2 size={36} className="text-emerald-500 animate-spin mb-3" />
                  <p className="text-gray-600">Generating monthly report...</p>
                </div>
              ) : reportData ? (
                <>
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        Consumption Report {reportData.year}
                      </h2>
                      <p className="text-sm text-gray-500">{reportData.period}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Month + Year pickers */}
                      <select
                        value={reportMonth}
                        onChange={(e) => setReportMonth(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                        data-testid="report-month-select"
                      >
                        {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                          <option key={m} value={i + 1}>{m}</option>
                        ))}
                      </select>
                      <select
                        value={reportYear}
                        onChange={(e) => setReportYear(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                        data-testid="report-year-select"
                      >
                        {Array.from({ length: 7 }, (_, i) => _now.getFullYear() - 5 + i).map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleLoadMonthlyReport()}
                        disabled={loadingReport}
                        className="px-3 py-2 bg-indigo-50 text-[#1a237e] border border-indigo-200 rounded-lg text-sm font-medium hover:bg-indigo-100 disabled:opacity-60"
                        data-testid="report-reload-btn"
                      >
                        {loadingReport ? 'Loading…' : 'View'}
                      </button>
                      <button
                        onClick={handleDownloadReport}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm flex items-center gap-2 hover:bg-emerald-600"
                        data-testid="download-report-btn"
                      >
                        <Download size={16} />
                        Download PDF
                      </button>
                    </div>
                  </div>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-600 font-medium">Opening Value</p>
                      <p className="text-lg font-bold text-blue-800">Rs. {(reportData.summary?.totalOpeningValue || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs text-green-600 font-medium">Purchase Value</p>
                      <p className="text-lg font-bold text-green-800">Rs. {(reportData.summary?.totalPurchaseValue || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3">
                      <p className="text-xs text-red-600 font-medium">Consumption Value</p>
                      <p className="text-lg font-bold text-red-800">Rs. {(reportData.summary?.totalConsumptionValue || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3">
                      <p className="text-xs text-amber-600 font-medium">Closing Value</p>
                      <p className="text-lg font-bold text-amber-800">Rs. {(reportData.summary?.totalClosingValue || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}</p>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mb-2">
                    {reportData.totalItems} items | {reportData.itemsInStock} in stock | {reportData.itemsZeroStock} zero stock
                  </p>
                  
                  {/* Report Table */}
                  <div className="border border-gray-200 rounded-lg overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-800 text-white">
                        <tr>
                          <th className="px-2 py-2 text-left w-8">SL</th>
                          <th className="px-2 py-2 text-left">ITEM NAME</th>
                          <th className="px-2 py-2 text-left">HSN</th>
                          <th className="px-2 py-2 text-right">OPENING</th>
                          <th className="px-2 py-2 text-right">PURCHASE</th>
                          <th className="px-2 py-2 text-right">CONSUMPTION</th>
                          <th className="px-2 py-2 text-right">CL STOCK</th>
                          <th className="px-2 py-2 text-center">UNIT</th>
                          <th className="px-2 py-2 text-right">RATE</th>
                          <th className="px-2 py-2 text-right">VALUE</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {reportData.items?.map((item, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-2 py-1.5 text-gray-500">{idx + 1}</td>
                            <td className="px-2 py-1.5 font-medium text-gray-800">{item.itemName}</td>
                            <td className="px-2 py-1.5 text-gray-500">{item.hsnCode}</td>
                            <td className="px-2 py-1.5 text-right">{item.opening > 0 ? item.opening.toLocaleString() : '-'}</td>
                            <td className="px-2 py-1.5 text-right text-green-600">{item.purchase > 0 ? item.purchase.toLocaleString() : '-'}</td>
                            <td className="px-2 py-1.5 text-right text-red-600">{item.consumption > 0 ? item.consumption.toLocaleString() : '-'}</td>
                            <td className={`px-2 py-1.5 text-right font-medium ${item.closing > 0 ? 'text-gray-900' : 'text-red-400'}`}>
                              {item.closing > 0 ? item.closing.toLocaleString() : '0'}
                            </td>
                            <td className="px-2 py-1.5 text-center text-gray-500">{item.unit}</td>
                            <td className="px-2 py-1.5 text-right">{item.rate > 0 ? `${item.rate.toFixed(2)}` : '-'}</td>
                            <td className="px-2 py-1.5 text-right font-medium">{item.value > 0 ? `Rs.${item.value.toLocaleString('en-IN', {maximumFractionDigits: 0})}` : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100 font-bold text-xs">
                        <tr>
                          <td className="px-2 py-2" colSpan={3}>TOTAL</td>
                          <td className="px-2 py-2 text-right">
                            Rs.{(reportData.summary?.totalOpeningValue || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
                          </td>
                          <td className="px-2 py-2 text-right text-green-700">
                            Rs.{(reportData.summary?.totalPurchaseValue || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
                          </td>
                          <td className="px-2 py-2 text-right text-red-700">
                            Rs.{(reportData.summary?.totalConsumptionValue || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
                          </td>
                          <td className="px-2 py-2 text-right">
                            Rs.{(reportData.summary?.totalClosingValue || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
                          </td>
                          <td colSpan={3}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              ) : (
                <div className="py-8">
                  <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                    <select
                      value={reportMonth}
                      onChange={(e) => setReportMonth(Number(e.target.value))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      data-testid="report-month-select-empty"
                    >
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                        <option key={m} value={i + 1}>{m}</option>
                      ))}
                    </select>
                    <select
                      value={reportYear}
                      onChange={(e) => setReportYear(Number(e.target.value))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      data-testid="report-year-select-empty"
                    >
                      {Array.from({ length: 7 }, (_, i) => _now.getFullYear() - 5 + i).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleLoadMonthlyReport()}
                      className="px-3 py-2 bg-[#1a237e] text-white rounded-lg text-sm font-medium hover:bg-[#283593]"
                      data-testid="report-reload-btn-empty"
                    >
                      Load Report
                    </button>
                  </div>
                  <p className="text-gray-500 text-center">No data available for the selected month. Pick a different month and click <strong>Load Report</strong>.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Material Issue Modal */}
      {showIssueModal && selectedItem && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowIssueModal(false)}></div>
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <button 
                onClick={() => setShowIssueModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
              
              <h2 className="text-xl font-bold text-gray-900 mb-4">Issue Material</h2>
              
              <div className="bg-amber-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>{selectedItem.itemName}</strong>
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Available: <strong>{selectedItem.availableQty?.toFixed(2)} {selectedItem.unit}</strong>
                </p>
              </div>
              
              <form onSubmit={handleIssueMaterial} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity to Issue *
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={selectedItem.availableQty}
                      value={issueForm.quantity}
                      onChange={(e) => setIssueForm({...issueForm, quantity: e.target.value})}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Enter quantity"
                      required
                    />
                    <span className="text-gray-500">{selectedItem.unit}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issued To *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      value={issueForm.issuedTo}
                      onChange={(e) => setIssueForm({...issueForm, issuedTo: e.target.value})}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Person/Team name"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <select
                      value={issueForm.department}
                      onChange={(e) => setIssueForm({...issueForm, department: e.target.value})}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 appearance-none bg-white"
                      data-testid="issue-department-select"
                    >
                      <option value="">Select Department</option>
                      <option value="Production">Production</option>
                      <option value="Raw Material">Raw Material</option>
                      <option value="Dispatch">Dispatch</option>
                      <option value="Stores">Stores</option>
                      <option value="Projects">Projects</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="HR">HR</option>
                      <option value="Imports">Imports</option>
                      <option value="Accounts and Office">Accounts and Office</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Purpose
                  </label>
                  <input
                    type="text"
                    value={issueForm.purpose}
                    onChange={(e) => setIssueForm({...issueForm, purpose: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="e.g., Machine repair, Regular use"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remarks
                  </label>
                  <textarea
                    value={issueForm.remarks}
                    onChange={(e) => setIssueForm({...issueForm, remarks: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    rows={2}
                    placeholder="Any additional notes"
                  ></textarea>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowIssueModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={issuing}
                    className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {issuing ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Issuing...
                      </>
                    ) : (
                      <>
                        <Minus size={16} />
                        Issue Material
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConsumableStock;
